/**
 * Player Dialogue System
 * Each player has unique personality-driven phrases for each action
 */

export interface PlayerDialogue {
    check: string[];
    call: string[];
    raise: string[];
    fold: string[];
}

export const PLAYER_DIALOGUE: Record<string, PlayerDialogue> = {
    // ACE - Aggressive cowboy with swagger and confidence
    ace: {
        check: [
            "Let's see what you got.",
            "I'll wait...for now.",
            "Slow and steady wins the race.",
            "Check, partner.",
            "Taking my time on this one.",
            "Easy does it.",
            "No need to rush.",
            "I'm in no hurry.",
            "Let's ride this out.",
            "Patience is a virtue."
        ],
        call: [
            "I'm in, let's dance!",
            "You're on!",
            "I'll match that bet.",
            "Count me in, partner.",
            "Call. Show me what you got.",
            "I'm not backing down!",
            "Let's see those cards!",
            "You got my attention.",
            "I'll bite.",
            "Deal me in!"
        ],
        raise: [
            "Time to turn up the heat!",
            "Raise! Let's make this interesting!",
            "I'm upping the ante!",
            "Too rich for your blood?",
            "Let's see who's got guts!",
            "Raising the stakes!",
            "Can you handle this?",
            "Double or nothing!",
            "Let's separate the men from the boys!",
            "This is where it gets fun!"
        ],
        fold: [
            "Not my hand.",
            "I'm out this round.",
            "Fold. Live to fight another day.",
            "This one's yours.",
            "Not worth the risk.",
            "I'll sit this one out.",
            "Save it for the next hand.",
            "Folding, but I'll be back.",
            "Not feeling it.",
            "Better hands are coming."
        ]
    },

    // LUNA - Tight, analytical, strategic
    luna: {
        check: [
            "Check. Analyzing...",
            "I'll observe for now.",
            "Check.",
            "Let's see how this develops.",
            "No action required yet.",
            "Checking.",
            "Patience.",
            "Observing the pattern.",
            "I'll wait.",
            "Check for now."
        ],
        call: [
            "The odds favor calling.",
            "I'll match that.",
            "Calculated call.",
            "The math checks out.",
            "Calling.",
            "I'll see that bet.",
            "Within acceptable range.",
            "Probability says call.",
            "Statistically sound.",
            "Call. The numbers work."
        ],
        raise: [
            "Raising. The odds are favorable.",
            "Optimal play: raise.",
            "Strategic raise.",
            "I'm raising.",
            "Time to increase pressure.",
            "Calculated aggression.",
            "Raising based on hand strength.",
            "The situation warrants a raise.",
            "Mathematically sound raise.",
            "Optimal raise here."
        ],
        fold: [
            "Not statistically viable.",
            "Folding. Poor odds.",
            "The math doesn't support continuing.",
            "Fold. Wrong spot.",
            "Negative expected value.",
            "Too risky given the odds.",
            "Folding this one.",
            "Not worth the investment.",
            "The probability isn't there.",
            "I fold."
        ]
    },

    // MAVERICK - Loose, risk-taking gambler
    maverick: {
        check: [
            "Check, but don't get comfortable!",
            "Free card? Sure!",
            "Check it is!",
            "Let's keep it moving!",
            "I'll check... this time.",
            "Check!",
            "Why not? Check!",
            "Let's see what happens!",
            "I'm checking, but I'm watching you!",
            "Check!"
        ],
        call: [
            "Let's gamble! I'm in!",
            "Call! Lady Luck is on my side!",
            "You bet I'm calling!",
            "I'll see that bet!",
            "Count me in!",
            "I'm feeling lucky - call!",
            "Let's do this! Call!",
            "Can't win if you don't play!",
            "Fortune favors the bold!",
            "I'm all in on this... call!"
        ],
        raise: [
            "Raise! Let's make it exciting!",
            "Feeling lucky! Raise!",
            "Life's a gamble - raise!",
            "Let's spice things up!",
            "Raising! Who's with me?",
            "Go big or go home!",
            "Raising the fun!",
            "More chips, more thrills!",
            "Let's see who's brave!",
            "Raise! Let's roll the dice!"
        ],
        fold: [
            "Fold. Even I have limits!",
            "Not this time, chief.",
            "Folding, but I'll be back!",
            "Can't win 'em all!",
            "Fold. Next hand's mine!",
            "I'm out...for now.",
            "Live to gamble another day!",
            "Fold. Boring hand anyway!",
            "Not my lucky hand.",
            "Folding, but watch out next time!"
        ]
    },

    // PROFESSOR - Cautious, methodical, wise
    professor: {
        check: [
            "Check. Prudent move.",
            "Checking is wise here.",
            "Check.",
            "I shall check.",
            "Observation mode: check.",
            "Check seems appropriate.",
            "A cautious check.",
            "Let's proceed carefully. Check.",
            "Check. Wisdom over haste.",
            "I check."
        ],
        call: [
            "A measured call.",
            "I'll call, cautiously.",
            "Call. Within risk tolerance.",
            "Acceptable risk. Call.",
            "Calling with consideration.",
            "I shall call.",
            "Call. Calculated decision.",
            "A prudent call.",
            "Calling. Risk assessed.",
            "I call."
        ],
        raise: [
            "A well-considered raise.",
            "Raising with confidence.",
            "Strategic raise required.",
            "I'm raising, but carefully.",
            "Calculated raise.",
            "The situation merits a raise.",
            "Raising thoughtfully.",
            "A precise raise.",
            "Deliberate raise.",
            "Raising. Hand warrants it."
        ],
        fold: [
            "Fold. Wisdom dictates retreat.",
            "Too risky. I fold.",
            "Folding is the wise play.",
            "Not worth the exposure. Fold.",
            "I must fold here.",
            "Discretion is the better part of valor.",
            "Fold. Unacceptable risk.",
            "Folding. Prudent decision.",
            "This warrants a fold.",
            "I fold."
        ]
    },

    // WILD CARD - Unpredictable, chaotic, theatrical
    wildcard: {
        check: [
            "Check! Or do I?",
            "Checking... for now!",
            "Ooh, a free card! Check!",
            "Check check check!",
            "Let's keep this mystery going! Check!",
            "Check! But I'm plotting something!",
            "Hehe, check!",
            "Checking! What chaos awaits?",
            "Check! The calm before the storm!",
            "I'll check, surprisingly!"
        ],
        call: [
            "Call! Chaos time!",
            "Why not? Call!",
            "Let's see what madness unfolds! Call!",
            "Call! The joker is in!",
            "Haha! Call!",
            "This should be fun! Call!",
            "Call! Let the games begin!",
            "Calling! Unpredictability engaged!",
            "Call! Who knows what happens next!",
            "I'm in! Call!"
        ],
        raise: [
            "Raise! Things just got interesting!",
            "Haha! Raising!",
            "Let's shake things up! Raise!",
            "Raise! Expect the unexpected!",
            "Time for chaos! Raising!",
            "Raising! Because why not?",
            "Surprise! Raise!",
            "Let's get WILD! Raising!",
            "Raise! Madness prevails!",
            "Plot twist: RAISE!"
        ],
        fold: [
            "Fold! Surprise!",
            "Out! For now...",
            "Folding! Or am I?",
            "Nope! Fold!",
            "Not this time! Fold!",
            "Fold! Saving my chaos for later!",
            "I'm out! Shocking, I know!",
            "Fold! But expect revenge!",
            "Folding! The plot thickens!",
            "Out! This time..."
        ]
    }
};

/**
 * Get a random dialogue line for a player and action
 */
export function getPlayerDialogue(playerName: string, action: string): string {
    const key = playerName.toLowerCase().replace(' ', '');
    const playerDialogue = PLAYER_DIALOGUE[key];

    if (!playerDialogue) {
        return action; // Fallback
    }

    const dialogueArray = playerDialogue[action as keyof PlayerDialogue];
    if (!dialogueArray || dialogueArray.length === 0) {
        return action; // Fallback
    }

    // Return a random phrase
    const randomIndex = Math.floor(Math.random() * dialogueArray.length);
    return dialogueArray[randomIndex];
}
